import React, { useState, useRef, useEffect, lazy } from 'react';
import { Link } from 'react-router';
import Title from '../../components/Title';

import { Gift, Handshake, Trophy, PenTool, CircleDollarSign } from 'lucide-react';
import SuperSuspense from '../../components/SuperSuspense';

const FAQ = lazy(() => import('./FAQ'));
const TestimonialSlider = lazy(() => import('./TestimonialSlider'));
const Analytics = lazy(() => import('../../components/Analytics'));

const Home = () =>
{
	const [topChallenge, setTopChallenge] = useState(false);
	const challengeRef = useRef();

	useEffect(() =>
	{
		const handleScroll = () =>
		{
			setTopChallenge(challengeRef.current?.getBoundingClientRect()?.top < 0);
		};

		handleScroll();
		window.addEventListener("scroll", handleScroll);
		window.addEventListener("resize", handleScroll);

		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	return (<>
	<div className="home navbar">
		<div className="logo"><img src="/images/logo/android-chrome-192x192.png" alt="Koala Quill" /> <p>Koala Quill</p></div>
		<div className={`toplink ${topChallenge?'topchallenge':'toplogin'}`}>
			<Link className="login" to="/login">Login</Link>
			<Link className="challenge button" to="/welcome">TAKE THE CHALLENGE</Link>
		</div>
	</div>

	<div className="home">
		<div className="hero-section">
			<div className="hero-section-image"> 
				<img src="/images/hero_write_together.avif" alt="A koala scribe earns a trophy as he writes" style={{display:'none'}} />
			</div>
			<div className="hero-section-text">
				<h1>Stop abandoning your drafts. Get paid just for showing up.</h1>
				<div className="hero-challenge">
					<p>Write 15 minutes daily for 7 days. Earn $7.</p>
					<div className="hero-section-buttons">
						<Link to="/welcome" ref={challengeRef} className="primary button">TAKE THE CHALLENGE</Link>
						<small><Gift /> Your first week is free, no credit card required</small>
					</div>
				</div>
			</div>
		</div>

		<div className="content-section">
			<div className="content-section-text">
				<h1 className="content-title">Write for 15 minutes, earn $1. Every day.</h1>
				<p>Other platforms ask you to write now and get paid eventually. Here, your $1 drops the moment you complete 15 minutes of writing.</p>
			</div>
			<div className="content-section-image">
				<img src="/images/write_every_day.png" alt="A koala writes furiously" />
			</div>
		</div>

		<div className="content-section">
			<div className="content-section-text">
				<h1 className="content-title">Write garbage. Write gold. Get paid either way.</h1>
				<p>Perfectionism has killed more novels than rejection letters. You can worry about quality later. Right now, your words just need to exist.</p>
			</div>
			<div className="content-section-image">
				<img src="/images/koala_trash.png" alt="A koala in a trash can" />
			</div>
		</div>

		<div className="content-section">
			<div className="content-section-text">
				<h1 className="content-title">Miss a day, lose money. Show up, collect what other writers forfeited.</h1>
				<p>We don't care why you skipped. Busy day, bad mood, forgot—the writer who took your dollar had to overcome these same excuses.</p>
			</div>
			<div className="content-section-image">
				<img src="/images/pants_fire.png" alt="A koala panics because his pants are on fire" />
			</div>
		</div>

		<div className="content-section">
			<div className="content-section-text">
				<h1 className="content-title">Can’t commit? Stay on the free plan forever.</h1>
				<p>Same accountability, smaller rewards. It won’t push you as hard, but it’ll still push you to write.</p>
			</div>
			<div className="content-section-image">
				<img src="/images/tropics.png" alt="A koala relaxes in the tropics" style={{maxWidth:'min(100%,300px)'}} />
			</div>
		</div>

		<div className="stat-section">
			<div className="stat-card">
				<Handshake />	
				<div className="stat-number">6K</div>
				<div className="stat-label">writing goals set</div>
			</div>
			<div className="stat-card">
				<Trophy />
				<div className="stat-number">89%</div>
				<div className="stat-label">writing goals kept</div>
			</div>
			<div className="stat-card">
				<PenTool />
				<div className="stat-number">36M</div>
				<div className="stat-label">words written</div>
			</div>
			<div className="stat-card">
				<CircleDollarSign />
				<div className="stat-number">49K</div>
				<div className="stat-label">dollars paid</div>
			</div>
		</div>
		<SuperSuspense><TestimonialSlider /></SuperSuspense>
		<SuperSuspense><FAQ /></SuperSuspense>

		<div className="final-section">
			<h1>Your next chapter starts here</h1>
			<div className="hero-challenge">
				<p>Write 15 minutes daily for 7 days. Earn $7.</p>
				<div className="hero-section-buttons">
					<Link to="/welcome" className="primary button" href="/welcome">TAKE THE CHALLENGE</Link>
					<small><Gift /> Your first week is free, no credit card required</small>
				</div>
			</div>
		</div>
		<ul className="footer">
			<li><a href="/faq">FAQ</a></li>
			<li><a href="/pricing">Pricing</a></li>
			<li><a href="/rules">Rules</a></li>
			<li><a href="/about">About</a></li>
			<li><a href="/ai-policy">AI Policy</a></li>
			<li><a href="/guilds">Community</a></li>
		</ul>
		<SuperSuspense><Analytics /></SuperSuspense>
		<Title>Get paid $1 for every day you write</Title>
	</div></>
	);
}

export default Home;
